import { Injectable, NgZone } from '@angular/core';
import { Router, NavigationEnd, Event as RouterEvent } from '@angular/router';
import { filter } from 'rxjs/operators';
import { trace, SpanStatusCode } from '@opentelemetry/api';
import { db } from '../../database/db';
import { contexteGel } from './gel-contexte';

interface PerformanceIssue {
	type: 'long_task' | 'memory_pressure' | 'indexeddb_slow' | 'event_loop_lag' | 'excessive_dom' | 'repeated_operation';
	duration?: number;
	threshold?: number;
	route: string;
	timestamp: number;
	memory?: MemoryInfo;
	domNodes?: number;
	pendingOperations?: number;
	details?: Record<string, any>;
	stackHint?: string;
	lastOperations?: string[];
	/** Ce que faisait l'utilisateur : fenêtre ouverte + dernière interaction (libellé, jamais le texte saisi). */
	action?: string;
}

interface MemoryInfo {
	usedJSHeapSize: number;
	totalJSHeapSize: number;
	jsHeapSizeLimit: number;
	usedMB: number;
	percentUsed: number;
}

interface OperationRecord {
	name: string;
	startTime: number;
	duration?: number;
	stack?: string;
}

interface OperationTracker {
	count: number;
	firstSeen: number;
	lastSeen: number;
}

@Injectable({
	providedIn: 'root'
})
export class PerformanceMonitorService {
	// Configuration
	// 250 ms : à 100 ms, Angular déclenche des long tasks en fonctionnement normal
	// (rendu de listes, détection de changement) → bruit constant. À 250 ms on ne
	// remonte que ce qui est réellement perceptible par l'utilisateur.
	private readonly LONG_TASK_THRESHOLD = 250; // ms - seuil pour les Long Tasks
	private readonly MEMORY_WARNING_PERCENT = 70; // % du heap utilisé
	private readonly MEMORY_CRITICAL_PERCENT = 85;
	private readonly DOM_NODES_WARNING = 10000;
	private readonly EVENT_LOOP_LAG_THRESHOLD = 100; // ms
	private readonly INDEXEDDB_SLOW_THRESHOLD = 500; // ms
	private readonly REPEATED_OP_THRESHOLD = 20; // même opération X fois (augmenté de 10 à 20)
	private readonly REPEATED_OP_WINDOW = 10000; // en Y ms (augmenté de 5s à 10s)
	private readonly REPORT_COOLDOWN = 30000; // Ne pas spammer la télémétrie (30s entre reports)

	// Opérations exclues du tracking de répétition (background sync normal)
	private readonly EXCLUDED_FROM_REPEAT_CHECK = [
		// waitingtransaction - opérations de sync en background
		'db.waitingtransaction.count',
		'db.waitingtransaction.toArray',
		'db.waitingtransaction.add',
		'db.waitingtransaction.delete',
		'db.waitingtransaction.put',
		// photo - opérations de sync en background  
		'db.photo.count',
		'db.photo.toArray'
	];

	// État
	private currentRoute = '/';
	private lastReportTime = 0;
	private isEnabled = true;
	private operationHistory: OperationRecord[] = [];
	private operationTrackers = new Map<string, OperationTracker>();
	private pendingOperations = new Map<string, OperationRecord>();
	private longTaskObserver: PerformanceObserver | null = null;
	private eventLoopCheckInterval: any = null;
	private memoryCheckInterval: any = null;
	private issueQueue: PerformanceIssue[] = [];

	/**
	 * Instants (performance.now) où l'appli a pu être suspendue : onglet masqué ou
	 * réaffiché, page gelée, mise en veille de l'appareil. Une tâche « longue » qui en
	 * contient un n'est pas un gel : c'est le temps passé suspendu (45 s « mesurées » au
	 * réveil d'un poste, 05/10).
	 */
	private suspensions: number[] = [];
	private derniereInteraction: { quoi: string; a: number; cible: EventTarget | null } | null = null;
	/** Gels au-delà de ce seuil : indicateur par utilisateur, comparable iPad / ordinateur. */
	private readonly SEUIL_GEL_MS = 2000;
	private readonly BILAN_TOUTES_MS = 15 * 60 * 1000;
	private bilan = { visibleMs: 0, depuis: 0, gels2s: 0, gels5s: 0, maxGelMs: 0, actionMaxGel: '', ignores: 0 };

	// Statistiques de session
	private sessionStats = {
		longTaskCount: 0,
		totalLongTaskDuration: 0,
		maxLongTaskDuration: 0,
		memoryWarnings: 0,
		slowIndexedDbOps: 0,
		repeatedOps: 0
	};

	constructor(
		private router: Router,
		private ngZone: NgZone
	) {}

	/**
	 * Initialise le monitoring global
	 */
	init(): void {
		if (!this.isEnabled) return;

		console.log('🔍 PerformanceMonitor: Initialisation...');

		// Track la route courante
		this.router.events.pipe(
			filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd)
		).subscribe((event) => {
			this.currentRoute = event.urlAfterRedirects;
		});

		// 1. Long Task API
		this.initLongTaskObserver();

		// 2. Event Loop Lag Detection
		this.initEventLoopMonitor();

		// 3. Memory Monitoring
		this.initMemoryMonitor();

		// 4. Wrapper IndexedDB (Dexie)
		this.wrapDexieOperations();

		// 5. Rapport périodique des stats
		this.initPeriodicReport();

		// 6. Suspensions (veille, onglet masqué), action en cours, bilan par utilisateur
		this.initSuspensions();
		this.initInteractions();

		console.log('✅ PerformanceMonitor: Initialisé');
	}

	/**
	 * Long Task API - Détecte quand le main thread est bloqué > 50ms
	 */
	private initLongTaskObserver(): void {
		if (!('PerformanceObserver' in window)) {
			console.warn('⚠️ PerformanceObserver non supporté');
			return;
		}

		try {
			this.longTaskObserver = new PerformanceObserver((list) => {
				for (const entry of list.getEntries()) {
					if (entry.duration > this.LONG_TASK_THRESHOLD) {
						// Évalué un peu plus tard : les événements de visibilité/réveil survenus
						// pendant la tâche ne sont traités qu'après elle.
						setTimeout(() => this.traiterLongTask(entry), 1500);
					}
				}
			});

			this.longTaskObserver.observe({ entryTypes: ['longtask'] });
			console.log('  ✓ Long Task Observer actif');
		} catch (e) {
			console.warn('⚠️ Long Task API non supportée:', e);
		}
	}

	private traiterLongTask(entry: PerformanceEntry): void {
		if (this.estSuspendu(entry.startTime, entry.startTime + entry.duration)) {
			this.bilan.ignores++;
			return;
		}
		this.sessionStats.longTaskCount++;
		this.sessionStats.totalLongTaskDuration += entry.duration;
		this.sessionStats.maxLongTaskDuration = Math.max(
			this.sessionStats.maxLongTaskDuration,
			entry.duration
		);

		// Capturer le contexte
		const issue: PerformanceIssue = {
			type: 'long_task',
			duration: Math.round(entry.duration),
			threshold: this.LONG_TASK_THRESHOLD,
			route: this.currentRoute,
			timestamp: Date.now(),
			memory: this.getMemoryInfo(),
			domNodes: this.getDomNodeCount(),
			pendingOperations: this.pendingOperations.size,
			lastOperations: this.getRecentOperations(),
			action: this.contexteAction(),
			details: {
				entryType: entry.entryType,
				name: entry.name,
				attribution: (entry as any).attribution?.map((a: any) => ({
					name: a.name,
					entryType: a.entryType,
					containerType: a.containerType,
					containerName: a.containerName
				}))
			}
		};

		// Log local pour debug
		if (entry.duration > 500) {
			console.warn(`🐌 Long Task détecté: ${Math.round(entry.duration)}ms sur ${this.currentRoute}`, issue);
		}

		this.queueIssue(issue);
	}

	/**
	 * Détecte le lag de l'event loop
	 */
	private initEventLoopMonitor(): void {
		let lastCheck = performance.now();
		let derniereHeure = Date.now();

		// Exécuter hors zone Angular pour ne pas déclencher de change detection
		this.ngZone.runOutsideAngular(() => {
			this.eventLoopCheckInterval = setInterval(() => {
				const now = performance.now();
				const heure = Date.now();
				const lag = now - lastCheck - 1000; // On attend 1000ms
				// Veille : l'heure réelle avance plus que l'horloge de la page (qui s'arrête
				// pendant la suspension sur la plupart des systèmes).
				if ((heure - derniereHeure) - (now - lastCheck) > 2000) this.suspensions.push(now);
				derniereHeure = heure;
				const debutFenetre = lastCheck;
				lastCheck = now;

				// Au-delà de 2 min, ce n'est plus un gel mesurable (onglet en arrière-plan,
				// timers bridés). En deçà, les suspensions (masqué, veille) sont écartées
				// ci-dessous — avant, tout lag > 10 s était ignoré, y compris les vrais gels
				// longs d'un iPad (seule mesure disponible sur Safari).
				const MAX_REALISTIC_LAG = 120000;

				if (lag > this.EVENT_LOOP_LAG_THRESHOLD && lag < MAX_REALISTIC_LAG) {
					// Onglet masqué AU MOMENT de la mesure : timers bridés (une fois par minute
					// sous Chrome), ce n'est pas un gel. À juger tout de suite — différé, le
					// contrôle voyait l'onglet déjà réaffiché et comptait ~59 s de « gel »
					// (4 postes BA, 05/10).
					if (document.visibilityState === 'hidden') { this.bilan.ignores++; }
					else setTimeout(() => this.traiterLag(lag, debutFenetre, now), 1500);
				}
			}, 1000);
		});

		console.log('  ✓ Event Loop Monitor actif');
	}

	private traiterLag(lag: number, debut: number, fin: number): void {
		if (this.estSuspendu(debut, fin)) { this.bilan.ignores++; return; }
		const action = this.contexteAction();
		if (lag >= this.SEUIL_GEL_MS) {
			this.bilan.gels2s++;
			if (lag >= 5000) this.bilan.gels5s++;
			if (lag > this.bilan.maxGelMs) { this.bilan.maxGelMs = Math.round(lag); this.bilan.actionMaxGel = action; }
		}
		const issue: PerformanceIssue = {
			type: 'event_loop_lag',
			duration: Math.round(lag),
			threshold: this.EVENT_LOOP_LAG_THRESHOLD,
			route: this.currentRoute,
			timestamp: Date.now(),
			memory: this.getMemoryInfo(),
			pendingOperations: this.pendingOperations.size,
			lastOperations: this.getRecentOperations(),
			action
		};

		console.warn(`⏱️ Event Loop Lag: ${Math.round(lag)}ms`, issue);
		this.queueIssue(issue);
		if (lag >= 5000) this.signalerGel(lag, debut, fin, action);
	}

	/**
	 * Gel de 5 s et plus : envoyé tout de suite, avec son contexte (focus, rafraîchissements
	 * du rapport pendant le gel, taille du dossier). But : distinguer une suspension par le
	 * système (Android, sans masquage de la page) d'un calcul de l'appli (19/10).
	 */
	private signalerGel(lag: number, debut: number, fin: number, action: string): void {
		try {
			const span = trace.getTracer('atlas-v2-web').startSpan('perf.gel');
			span.setAttribute('gel.duree_ms', Math.round(lag));
			span.setAttribute('gel.route', this.currentRoute);
			span.setAttribute('gel.appareil', this.typeAppareil());
			if (action) span.setAttribute('gel.action', action);
			const memoire = this.getMemoryInfo();
			if (memoire?.usedMB != null) span.setAttribute('gel.memoire_mo', memoire.usedMB);
			span.setAttribute('gel.operations', this.getRecentOperations().slice(-5).join(' | '));
			for (const [cle, valeur] of Object.entries(contexteGel(debut, fin))) span.setAttribute(cle, valeur);
			const u = this.getUserContext();
			if (u.username) span.setAttribute('user.name', u.username);
			if (u.email) span.setAttribute('user.email', u.email);
			span.end();
		} catch {
			// Mesure seulement.
		}
	}

	/** L'intervalle [debut, fin] (performance.now) contient-il une suspension de la page ? */
	private estSuspendu(debut: number, fin: number): boolean {
		if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return true;
		return this.suspensions.some(t => t >= debut - 1000 && t <= fin + 2000);
	}

	private initSuspensions(): void {
		if (typeof document === 'undefined') return;
		this.bilan.depuis = document.visibilityState === 'visible' ? performance.now() : 0;
		const noter = () => {
			this.suspensions.push(performance.now());
			if (this.suspensions.length > 50) this.suspensions.splice(0, this.suspensions.length - 50);
		};
		document.addEventListener('visibilitychange', () => {
			noter();
			if (document.visibilityState === 'visible') {
				this.bilan.depuis = performance.now();
			} else {
				this.cumulerVisible();
				if (this.bilan.visibleMs >= 60_000) this.envoyerBilan();
			}
		});
		document.addEventListener('freeze', noter);
		document.addEventListener('resume', noter);
		window.addEventListener('pageshow', noter);
		window.addEventListener('pagehide', () => {
			this.cumulerVisible();
			if (this.bilan.visibleMs >= 60_000) this.envoyerBilan();
		});
		this.ngZone.runOutsideAngular(() => {
			setInterval(() => {
				this.cumulerVisible();
				if (this.bilan.visibleMs >= this.BILAN_TOUTES_MS) this.envoyerBilan();
			}, 60_000);
		});
	}

	private cumulerVisible(): void {
		if (this.bilan.depuis) {
			const maintenant = performance.now();
			this.bilan.visibleMs += maintenant - this.bilan.depuis;
			this.bilan.depuis = document.visibilityState === 'visible' ? maintenant : 0;
		}
	}

	/**
	 * Bilan par utilisateur : gels de plus de 2 s par temps d'usage réel (appli à l'écran).
	 * Mesuré par le retard de la boucle d'événements, disponible sur iPad comme sur
	 * ordinateur (Safari ne fournit pas les « long tasks ») — donc comparable partout.
	 */
	private envoyerBilan(): void {
		try {
			const span = trace.getTracer('atlas-v2-web').startSpan('perf.bilan');
			const u = this.getUserContext();
			if (u.username) span.setAttribute('user.name', u.username);
			if (u.email) span.setAttribute('user.email', u.email);
			span.setAttribute('bilan.visible_min', Math.round(this.bilan.visibleMs / 6000) / 10);
			span.setAttribute('bilan.gels_2s', this.bilan.gels2s);
			span.setAttribute('bilan.gels_5s', this.bilan.gels5s);
			span.setAttribute('bilan.max_gel_ms', this.bilan.maxGelMs);
			if (this.bilan.actionMaxGel) span.setAttribute('bilan.action_max_gel', this.bilan.actionMaxGel);
			span.setAttribute('bilan.ignores_suspension', this.bilan.ignores);
			span.setAttribute('bilan.appareil', this.typeAppareil());
			span.setAttribute('perf.route', this.currentRoute);
			span.end();
		} catch { /* mesure seulement */ }
		const depuis = this.bilan.depuis;
		this.bilan = { visibleMs: 0, depuis, gels2s: 0, gels5s: 0, maxGelMs: 0, actionMaxGel: '', ignores: 0 };
	}

	private typeAppareil(): string {
		const ua = navigator.userAgent || '';
		if (/iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'ipad';
		if (/iPhone/.test(ua)) return 'iphone';
		if (/Android/.test(ua)) return 'android';
		return 'ordinateur';
	}

	/**
	 * Dernière interaction (clic, champ en saisie) : son LIBELLÉ seulement — bouton, nom du
	 * champ — jamais le texte tapé. Permet de dire « gel pendant tel bouton / telle fenêtre ».
	 */
	private initInteractions(): void {
		if (typeof document === 'undefined') return;
		const noter = (e: Event) => {
			const d = this.derniereInteraction;
			// Frappe dans le même champ : on met juste l'instant à jour (pas de recalcul).
			if (e.type === 'keydown' && d && d.cible === e.target) { d.a = performance.now(); return; }
			try {
				this.derniereInteraction = { quoi: this.decrire(e.target, e.type === 'keydown'), a: performance.now(), cible: e.target };
			} catch { /* mesure seulement */ }
		};
		document.addEventListener('pointerdown', noter, { capture: true, passive: true });
		document.addEventListener('keydown', noter, { capture: true, passive: true });
	}

	private decrire(cible: EventTarget | null, frappe: boolean): string {
		const base = cible as HTMLElement | null;
		const el = base?.closest?.('button,a,[role=button],[role=menuitem],[role=tab],mat-option,mat-checkbox,mat-radio-button,mat-select,input,textarea,[contenteditable=true],.ck-editor,mat-icon') as HTMLElement | null;
		if (!el) return `${frappe ? 'frappe' : 'clic'}:${base?.tagName?.toLowerCase() ?? '?'}`;
		const champ = el.matches('input,textarea,[contenteditable=true],.ck-editor,mat-select');
		let libelle = el.getAttribute('aria-label') || el.getAttribute('title') || el.getAttribute('mattooltip') || '';
		if (!libelle) {
			libelle = champ
				? (el.getAttribute('formcontrolname') || el.getAttribute('placeholder') || el.getAttribute('name') || (el.closest('.ck-editor') ? 'éditeur' : ''))
				: (el.textContent || '');
		}
		libelle = libelle.replace(/\s+/g, ' ').trim().slice(0, 40);
		return `${champ ? 'champ' : 'clic'}:${libelle || el.tagName.toLowerCase()}`;
	}

	/** Fenêtre ouverte et dernière interaction (détecteur d'arrêts brutaux). */
	actionEnCours(): string {
		return this.contexteAction();
	}

	/** Fenêtre ouverte (composant du dialogue) + dernière interaction, pour la télémétrie. */
	private contexteAction(): string {
		try {
			const dialogues = document.querySelectorAll('mat-dialog-container');
			const dernier = dialogues[dialogues.length - 1] as HTMLElement | undefined;
			const composant = dernier?.querySelector('.mat-mdc-dialog-surface > *, .mat-mdc-dialog-container > *')?.tagName?.toLowerCase()
				?? dernier?.firstElementChild?.tagName?.toLowerCase();
			const d = this.derniereInteraction;
			const interaction = d ? `${d.quoi} (il y a ${Math.round((performance.now() - d.a) / 1000)} s)` : 'aucune interaction';
			return (composant ? `${composant} | ` : '') + interaction;
		} catch {
			return '';
		}
	}

	/**
	 * Surveille la mémoire
	 */
	private initMemoryMonitor(): void {
		if (!(performance as any).memory) {
			console.warn('⚠️ performance.memory non supporté (Chrome only)');
			return;
		}

		this.ngZone.runOutsideAngular(() => {
			this.memoryCheckInterval = setInterval(() => {
				const memory = this.getMemoryInfo();
				if (!memory) return;

				if (memory.percentUsed > this.MEMORY_CRITICAL_PERCENT) {
					this.sessionStats.memoryWarnings++;

					const issue: PerformanceIssue = {
						type: 'memory_pressure',
						route: this.currentRoute,
						timestamp: Date.now(),
						memory,
						domNodes: this.getDomNodeCount(),
						pendingOperations: this.pendingOperations.size,
						details: {
							severity: 'critical',
							percentUsed: memory.percentUsed
						}
					};

					console.error(`🚨 Mémoire critique: ${memory.usedMB}MB (${memory.percentUsed}%)`, issue);
					this.queueIssue(issue);

				} else if (memory.percentUsed > this.MEMORY_WARNING_PERCENT) {
					// Juste un log, pas de report
					console.warn(`⚠️ Mémoire élevée: ${memory.usedMB}MB (${memory.percentUsed}%)`);
				}
			}, 10000); // Check toutes les 10s
		});

		console.log('  ✓ Memory Monitor actif');
	}

	/**
	 * Wrapper les opérations Dexie pour détecter les lenteurs
	 * On ne track que les méthodes "terminales" qui retournent des Promises
	 */
	private wrapDexieOperations(): void {
		if (!db) {
			console.warn('⚠️ Dexie db non disponible');
			return;
		}

		const self = this;
		const originalTable = db.table.bind(db);
		const wrappedTables = new Map<string, any>(); // Cache des tables déjà wrappées

		// Méthodes terminales qui retournent des Promises (celles qu'on veut tracker)
		const terminalMethods = ['get', 'put', 'add', 'delete', 'bulkPut', 'bulkAdd', 'bulkDelete', 'toArray', 'count', 'first', 'last'];
		
		// Méthodes de chaînage qui retournent des Collections (on ne les track pas pour éviter les faux positifs)
		// const chainMethods = ['where', 'filter', 'orderBy', 'reverse', 'offset', 'limit'];

		// On wrap la méthode table() pour intercepter les opérations
		(db as any).table = function(tableName: string) {
			// Retourner la table déjà wrappée si elle existe
			if (wrappedTables.has(tableName)) {
				return wrappedTables.get(tableName);
			}

			const table = originalTable(tableName);
			
			for (const method of terminalMethods) {
				if (typeof (table as any)[method] === 'function') {
					const original = (table as any)[method].bind(table);
					(table as any)[method] = function(...args: any[]) {
						const opName = `db.${tableName}.${method}`;
						const opId = self.startOperation(opName);
						
						const result = original(...args);
						
						// Ces méthodes retournent toujours des Promises
						if (result && typeof result.then === 'function') {
							return result.then((res: any) => {
								self.endOperation(opId, opName);
								return res;
							}).catch((err: any) => {
								self.endOperation(opId, opName, err);
								throw err;
							});
						}
						
						// Fallback au cas où
						self.endOperation(opId, opName);
						return result;
					};
				}
			}
			
			// Mettre en cache la table wrappée
			wrappedTables.set(tableName, table);
			return table;
		};

		console.log('  ✓ Dexie wrapper actif');
	}

	/**
	 * Démarre le tracking d'une opération
	 */
	startOperation(name: string): string {
		const id = `${name}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
		
		// Capturer la stack pour debug
		let stack: string | undefined;
		try {
			stack = new Error().stack?.split('\n').slice(2, 6).join('\n');
		} catch {}

		this.pendingOperations.set(id, {
			name,
			startTime: performance.now(),
			stack
		});

		// Skip le tracking de répétition pour les opérations de background sync
		if (this.EXCLUDED_FROM_REPEAT_CHECK.includes(name)) {
			return id;
		}

		// Tracker les répétitions
		const tracker = this.operationTrackers.get(name) || { count: 0, firstSeen: Date.now(), lastSeen: Date.now() };
		tracker.count++;
		tracker.lastSeen = Date.now();
		
		// Reset si la fenêtre est passée
		if (Date.now() - tracker.firstSeen > this.REPEATED_OP_WINDOW) {
			tracker.count = 1;
			tracker.firstSeen = Date.now();
		}
		
		this.operationTrackers.set(name, tracker);

		// Détecter les opérations répétitives
		if (tracker.count >= this.REPEATED_OP_THRESHOLD) {
			this.sessionStats.repeatedOps++;
			
			const issue: PerformanceIssue = {
				type: 'repeated_operation',
				route: this.currentRoute,
				timestamp: Date.now(),
				details: {
					operationName: name,
					count: tracker.count,
					windowMs: this.REPEATED_OP_WINDOW
				},
				stackHint: stack
			};

			console.warn(`🔄 Opération répétée ${tracker.count}x: ${name}`, issue);
			this.queueIssue(issue);

			// Reset pour éviter de spammer
			tracker.count = 0;
			tracker.firstSeen = Date.now();
		}

		return id;
	}

	/**
	 * Termine le tracking d'une opération
	 */
	endOperation(id: string, name: string, error?: any): void {
		const op = this.pendingOperations.get(id);
		if (!op) return;

		const duration = performance.now() - op.startTime;
		this.pendingOperations.delete(id);

		// Ajouter à l'historique
		this.operationHistory.push({
			name,
			startTime: op.startTime,
			duration
		});

		// Garder seulement les 50 dernières
		if (this.operationHistory.length > 50) {
			this.operationHistory.shift();
		}

		// Détecter les opérations IndexedDB lentes
		if (name.startsWith('db.') && duration > this.INDEXEDDB_SLOW_THRESHOLD) {
			this.sessionStats.slowIndexedDbOps++;

			const issue: PerformanceIssue = {
				type: 'indexeddb_slow',
				duration: Math.round(duration),
				threshold: this.INDEXEDDB_SLOW_THRESHOLD,
				route: this.currentRoute,
				timestamp: Date.now(),
				details: {
					operation: name,
					error: error?.message
				},
				stackHint: op.stack
			};

			console.warn(`💾 IndexedDB lent: ${name} (${Math.round(duration)}ms)`, issue);
			this.queueIssue(issue);
		}
	}

	/**
	 * Méthode publique pour tracker manuellement une opération async
	 */
	async trackAsync<T>(name: string, operation: () => Promise<T>): Promise<T> {
		const opId = this.startOperation(name);
		try {
			const result = await operation();
			this.endOperation(opId, name);
			return result;
		} catch (error) {
			this.endOperation(opId, name, error);
			throw error;
		}
	}

	/**
	 * Méthode publique pour tracker une opération sync
	 */
	trackSync<T>(name: string, operation: () => T): T {
		const opId = this.startOperation(name);
		try {
			const result = operation();
			this.endOperation(opId, name);
			return result;
		} catch (error) {
			this.endOperation(opId, name, error);
			throw error;
		}
	}

	/**
	 * Queue un issue pour envoi groupé
	 */
	private queueIssue(issue: PerformanceIssue): void {
		this.issueQueue.push(issue);

		// Envoyer si on a assez d'issues ou si c'est critique
		if (issue.type === 'memory_pressure' && issue.details?.['severity'] === 'critical') {
			this.flushIssues();
		} else if (this.issueQueue.length >= 5) {
			this.flushIssues();
		}
	}

	/**
	 * Envoie les issues à OpenObserve via OpenTelemetry (relayées par l'API)
	 */
	private flushIssues(): void {
		if (this.issueQueue.length === 0) return;

		const now = Date.now();
		if (now - this.lastReportTime < this.REPORT_COOLDOWN) {
			// Trop tôt, on garde en queue
			return;
		}

		this.lastReportTime = now;

		// Grouper par type
		const issuesByType = new Map<string, PerformanceIssue[]>();
		for (const issue of this.issueQueue) {
			const list = issuesByType.get(issue.type) || [];
			list.push(issue);
			issuesByType.set(issue.type, list);
		}

		// Créer un rapport agrégé
		const report = {
			timestamp: now,
			route: this.currentRoute,
			sessionStats: { ...this.sessionStats },
			issues: Object.fromEntries(issuesByType),
			memory: this.getMemoryInfo(),
			domNodes: this.getDomNodeCount()
		};

		// Si problèmes critiques, on marque le span en erreur
		const hasCritical = this.issueQueue.some(i => 
			i.type === 'memory_pressure' || 
			(i.type === 'long_task' && (i.duration || 0) > 1000)
		);

		if (hasCritical) {
			this.sendTelemetry('performance.degradation', report, true);
		} else {
			this.sendTelemetry('performance.issues', report, false);
		}

		console.log('📊 Performance report sent:', report);

		// Vider la queue
		this.issueQueue = [];
	}

	/**
	 * Envoie un événement de performance via OpenTelemetry
	 */
	private sendTelemetry(eventName: string, data: any, isError: boolean): void {
		try {
			const tracer = trace.getTracer('atlas-v2-web');
			const span = tracer.startSpan(`perf.${eventName}`);

			// User context
			const userContext = this.getUserContext();
			if (userContext.username) span.setAttribute('user.name', userContext.username);
			if (userContext.email) span.setAttribute('user.email', userContext.email);

			// Performance data
			span.setAttribute('perf.route', this.currentRoute);
			span.setAttribute('perf.issueCount', this.issueQueue.length);
			span.setAttribute('perf.longTaskCount', this.sessionStats.longTaskCount);
			span.setAttribute('perf.maxLongTaskDuration', this.sessionStats.maxLongTaskDuration);
			span.setAttribute('perf.slowIndexedDbOps', this.sessionStats.slowIndexedDbOps);
			span.setAttribute('perf.repeatedOps', this.sessionStats.repeatedOps);
			span.setAttribute('perf.memoryWarnings', this.sessionStats.memoryWarnings);

			// Memory info
			const memory = this.getMemoryInfo();
			if (memory) {
				span.setAttribute('perf.memory.usedMB', memory.usedMB);
				span.setAttribute('perf.memory.percentUsed', memory.percentUsed);
			}

			// DOM info
			span.setAttribute('perf.domNodes', this.getDomNodeCount());
			span.setAttribute('perf.pendingOperations', this.pendingOperations.size);

			// Issue types présents
			const issueTypes = [...new Set(this.issueQueue.map(i => i.type))];
			span.setAttribute('perf.issueTypes', issueTypes.join(','));

			// Dernières opérations
			span.setAttribute('perf.lastOperations', this.getRecentOperations().join(' | '));

			// Action en cours lors du plus long problème du lot
			const pire = [...this.issueQueue].filter(i => i.action).sort((a, b) => (b.duration ?? 0) - (a.duration ?? 0))[0];
			if (pire?.action) span.setAttribute('perf.action', pire.action);

			// Full report en JSON pour debug
			span.setAttribute('perf.report', JSON.stringify(data));

			if (isError) {
				span.setStatus({ code: SpanStatusCode.ERROR, message: 'Performance degradation detected' });
				span.recordException(new Error(`Performance issues: ${issueTypes.join(', ')}`));
			}

			span.end();
		} catch (e) {
			console.error('Échec envoi télémétrie:', e);
		}
	}

	/**
	 * Helper to get user context
	 */
	private getUserContext(): { username?: string; email?: string } {
		try {
			const stable = localStorage.getItem('sentryUser');
			if (stable) {
				try {
					const u = JSON.parse(stable);
					return { username: u?.username || u?.name, email: u?.email };
				} catch {
					return { username: stable };
				}
			}
		} catch {}
		return {};
	}

	/**
	 * Rapport périodique des stats de session
	 */
	private initPeriodicReport(): void {
		this.ngZone.runOutsideAngular(() => {
			setInterval(() => {
				if (this.sessionStats.longTaskCount > 0 || this.sessionStats.slowIndexedDbOps > 0) {
					console.log('📈 Performance Stats:', {
						...this.sessionStats,
						currentMemory: this.getMemoryInfo(),
						domNodes: this.getDomNodeCount(),
						pendingOps: this.pendingOperations.size
					});

					// Flush les issues en attente
					this.flushIssues();
				}
			}, 60000); // Toutes les minutes
		});
	}

	/**
	 * Récupère les infos mémoire
	 */
	private getMemoryInfo(): MemoryInfo | undefined {
		const memory = (performance as any).memory;
		if (!memory) return undefined;

		return {
			usedJSHeapSize: memory.usedJSHeapSize,
			totalJSHeapSize: memory.totalJSHeapSize,
			jsHeapSizeLimit: memory.jsHeapSizeLimit,
			usedMB: Math.round(memory.usedJSHeapSize / 1024 / 1024),
			percentUsed: Math.round((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100)
		};
	}

	/**
	 * Compte les noeuds DOM
	 */
	private getDomNodeCount(): number {
		try {
			return document.querySelectorAll('*').length;
		} catch {
			return -1;
		}
	}

	/**
	 * Récupère les dernières opérations
	 */
	private getRecentOperations(): string[] {
		return this.operationHistory
			.slice(-10)
			.map(op => `${op.name} (${Math.round(op.duration || 0)}ms)`);
	}

	/**
	 * Active/désactive le monitoring
	 */
	setEnabled(enabled: boolean): void {
		this.isEnabled = enabled;
		
		if (!enabled) {
			this.destroy();
		}
	}

	/**
	 * Récupère les stats de session
	 */
	getSessionStats() {
		return {
			...this.sessionStats,
			currentMemory: this.getMemoryInfo(),
			domNodes: this.getDomNodeCount(),
			pendingOperations: this.pendingOperations.size,
			recentOperations: this.getRecentOperations()
		};
	}

	/**
	 * Force un check et report immédiat
	 */
	forceReport(): void {
		const stats = this.getSessionStats();
		console.log('📊 Forced Performance Report:', stats);

		this.sendTelemetry('manual_report', stats, false);
	}

	/**
	 * Nettoie les ressources
	 */
	destroy(): void {
		if (this.longTaskObserver) {
			this.longTaskObserver.disconnect();
			this.longTaskObserver = null;
		}

		if (this.eventLoopCheckInterval) {
			clearInterval(this.eventLoopCheckInterval);
			this.eventLoopCheckInterval = null;
		}

		if (this.memoryCheckInterval) {
			clearInterval(this.memoryCheckInterval);
			this.memoryCheckInterval = null;
		}

		console.log('🔍 PerformanceMonitor: Détruit');
	}
}
